// Integração com a API pública Comunica PJe / DJEN (Diário de Justiça Eletrônico Nacional - CNJ)
// Documentação: https://comunicaapi.pje.jus.br/swagger/index.html

const API_URL = 'https://comunicaapi.pje.jus.br/api/v1/comunicacao';

export interface ComunicacaoPje {
  id: number;
  data_disponibilizacao: string; // AAAA-MM-DD
  siglaTribunal: string;
  tipoComunicacao: string;
  nomeOrgao: string;
  texto: string;
  numero_processo: string; // apenas dígitos
  numeroprocessocommascara: string;
  link: string | null;
  tipoDocumento: string | null;
  nomeClasse: string | null;
  meiocompleto: string | null;
  destinatarios: { nome: string; polo: string }[];
}

interface ApiResponse {
  status: string;
  message: string;
  count: number;
  items: ComunicacaoPje[];
}

const formatDate = (d: Date) => d.toISOString().split('T')[0];

export const onlyDigits = (value?: string | null) => (value || '').replace(/\D/g, '');

/**
 * Busca as comunicações (intimações/publicações) de uma OAB no período informado.
 * Percorre todas as páginas da API.
 */
export async function fetchComunicacoesPorOab(
  numeroOab: string,
  ufOab: string,
  inicio: Date,
  fim: Date
): Promise<ComunicacaoPje[]> {
  const itensPorPagina = 100;
  const all: ComunicacaoPje[] = [];

  for (let pagina = 1; pagina <= 20; pagina++) {
    const params = new URLSearchParams({
      numeroOab: onlyDigits(numeroOab),
      ufOab: ufOab.toUpperCase(),
      dataDisponibilizacaoInicio: formatDate(inicio),
      dataDisponibilizacaoFim: formatDate(fim),
      itensPorPagina: String(itensPorPagina),
      pagina: String(pagina),
    });

    const response = await fetch(`${API_URL}?${params}`, { headers: { Accept: 'application/json' } });
    if (response.status === 429) {
      throw new Error('Limite de consultas do CNJ atingido. Aguarde um minuto e tente novamente.');
    }
    if (!response.ok) {
      throw new Error(`Erro ao consultar o Comunica PJe (HTTP ${response.status})`);
    }

    const data: ApiResponse = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || 'O Comunica PJe não respondeu. Tente novamente mais tarde.');
    }

    all.push(...data.items);
    if (data.items.length < itensPorPagina || all.length >= data.count) break;
  }

  // A API pode repetir a mesma comunicação; remove duplicadas pelo id
  const unique = new Map(all.map((item) => [item.id, item]));
  return [...unique.values()].sort((a, b) =>
    b.data_disponibilizacao.localeCompare(a.data_disponibilizacao)
  );
}

// O DJEN começou a receber comunicações em 2023; antes disso a API não tem dados
const DJEN_START = new Date('2023-01-01T00:00:00Z');

/**
 * Busca todo o histórico de comunicações da OAB, em janelas de um ano.
 */
export async function fetchTodasComunicacoesPorOab(numeroOab: string, ufOab: string): Promise<ComunicacaoPje[]> {
  const all: ComunicacaoPje[] = [];
  let fim = new Date();
  while (fim > DJEN_START) {
    const inicio = new Date(Math.max(fim.getTime() - 365 * 24 * 60 * 60 * 1000, DJEN_START.getTime()));
    all.push(...(await fetchComunicacoesPorOab(numeroOab, ufOab, inicio, fim)));
    fim = new Date(inicio.getTime() - 24 * 60 * 60 * 1000);
  }
  const unique = new Map(all.map((item) => [item.id, item]));
  return [...unique.values()].sort((a, b) =>
    b.data_disponibilizacao.localeCompare(a.data_disponibilizacao)
  );
}
